import { useState, type ReactNode } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { FormattedMessage } from "react-intl";

import AlertMessage from "common/core/alert_message";
import Link from "common/core/link";
import { newBrandAnnouncementOrgUrl } from "constants/marketing";
import {
  useRebrandLoginBanner,
  usePhase2RebrandLoginBanner,
  usePhase3RebrandLoginBanner,
} from "util/feature_detection";
import { usePortalMappingBannerMessage } from "common/proof_frame/banners/phase2_rebrand_org_banner";

const ANNOUNCEMENT_URL = `${newBrandAnnouncementOrgUrl}?utm_source=notarize&utm_medium=banner&utm_campaign=proof-launch&utm_content=login`;

function useInUserFlow() {
  const [searchParams] = useSearchParams();
  const hasFlowParams = searchParams.get("email") || searchParams.get("transaction_id");
  const { pathname } = useLocation();
  const isResettingPassword = pathname.includes("reset_password");

  return Boolean(hasFlowParams) || isResettingPassword;
}

function BrandLoginBanner() {
  const useNewBrandBanner = useRebrandLoginBanner();
  const usePhase2Banner = usePhase2RebrandLoginBanner();
  const usePhase3Banner = usePhase3RebrandLoginBanner();
  const bannerMessage = usePortalMappingBannerMessage();
  const inUserFlow = useInUserFlow();
  const [showBanner, setShowBanner] = useState(
    !inUserFlow && (useNewBrandBanner || usePhase2Banner || usePhase3Banner),
  );

  if (!showBanner) {
    return null;
  } else if (usePhase3Banner && !window.location.hostname.endsWith("notarize.com")) {
    return (
      <AlertMessage
        banner
        kind="announcement"
        centerText
        onClose={() => setShowBanner(false)}
        customEmoji="🎊"
      >
        <FormattedMessage
          id="accc80d8-0112-47b2-b4b6-f8279b539341"
          defaultMessage="<b>Proof is here!</b> Your Notarize credentials are now your Proof credentials."
          values={{
            b: (text: ReactNode) => <strong>{text}</strong>,
          }}
        />
      </AlertMessage>
    );
  } else if (useNewBrandBanner) {
    return (
      <AlertMessage banner kind="announcement" centerText onClose={() => setShowBanner(false)}>
        <FormattedMessage
          id="6b24256c-b2b8-4a7c-ab57-7193894939dc"
          defaultMessage="<newLook>Introducing Proof!</newLook> Together, Notarize and Proof bring trust to every transaction. <learnMore>Learn more</learnMore>"
          values={{
            newLook: (text: ReactNode) => <strong>{text}</strong>,
            learnMore: (text: ReactNode) => (
              <Link white href={ANNOUNCEMENT_URL}>
                {text}
              </Link>
            ),
          }}
        />
      </AlertMessage>
    );
  } else if (usePhase2Banner) {
    return (
      <AlertMessage banner kind="announcement" centerText onClose={() => setShowBanner(false)}>
        {bannerMessage}
      </AlertMessage>
    );
  }
  return null;
}

export default BrandLoginBanner;
